<template>
  <div>
    <template v-if="claudePreview">
      <div class="d-flex justify-center">
        <VCard outlined class="pa-3" max-width="400">
          <VCardTitle class="subtitle-2 pa-0 pb-2">
            Claude Preview Mode
          </VCardTitle>

          <VCardText class="pa-0 text-left">
            <VAlert type="info" text dense class="mb-3 caption">
              OAuth popups are blocked in Claude Preview. Authenticate in a real
              browser, then paste your tokens here.
            </VAlert>

            <div class="caption mb-2">
              <strong>1.</strong> Open
              <a :href="origin" target="_blank">{{ origin }}</a>
              in your browser<br />
              <strong>2.</strong> Click "Connect to Salesforce" and log in<br />
              <strong>3.</strong> Right-click anywhere and choose
              <strong>Copy Token</strong><br />
              <strong>4.</strong> Paste below
            </div>

            <VTextarea
              v-model="tokenPayload"
              outlined
              dense
              rows="3"
              auto-grow
              placeholder='{"accessToken":"...","refreshToken":"..."}'
              style="font-family: monospace; font-size: 11px"
              :error-messages="parseError ? [parseError] : []"
              hide-details="auto"
              :loading="isImporting"
            />
          </VCardText>
        </VCard>
      </div>
    </template>

    <template v-else>
      <VBtn color="primary" :loading="isConnecting" @click="connectSalesforce">
        <VIcon left>$salesforce</VIcon>
        Connect to Salesforce
      </VBtn>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue'
import { getModule } from 'vuex-module-decorators'

import { trackLogin } from '@/components/mixins/analytics'
import Salesforce from '@/store/modules/salesforce'
import { isClaudePreview } from '@/utils/claudePreview'

export default defineComponent({
  setup() {
    const salesforce = getModule(Salesforce)
    const claudePreview = isClaudePreview()

    // Normal OAuth flow
    const isConnecting = ref(false)

    async function connectSalesforce() {
      isConnecting.value = true
      await salesforce
        .getAccessToken()
        .finally(() => (isConnecting.value = false))
      trackLogin({
        method: 'Salesforce'
      })
    }

    // Claude Preview token import flow
    const tokenPayload = ref('')
    const parseError = ref<string | null>(null)
    const isImporting = ref(false)

    async function importTokens() {
      isImporting.value = true
      try {
        const parsed = JSON.parse(tokenPayload.value)
        await salesforce.setAccessTokenAndConfigureTokenRefresh(
          parsed.accessToken
        )
        salesforce.setRefreshToken(parsed.refreshToken)
      } finally {
        isImporting.value = false
      }
    }

    watch(tokenPayload, (value) => {
      if (!value.trim()) {
        parseError.value = null
        return
      }
      try {
        const parsed = JSON.parse(value)
        if (!parsed.accessToken || !parsed.refreshToken) {
          parseError.value = 'Missing accessToken or refreshToken'
        } else {
          parseError.value = null
          importTokens()
        }
      } catch {
        parseError.value = 'Invalid JSON'
      }
    })

    return {
      claudePreview,
      connectSalesforce,
      isConnecting,
      tokenPayload,
      parseError,
      isImporting,
      origin: location.origin
    }
  }
})
</script>
